// Site-wide content data - Multi-product architecture

export const siteContent = {
  brand: {
    name: 'Klarhead',
    tagline: 'Operations, Clarified',
  },

  products: {
    psa: {
      name: 'Klarhead PSA',
      fullName: 'Professional Services Automation',
      tagline: 'Professional Services, Clarified',
      slug: 'psa',
      accent: 'teal',
      hero: {
        headline: "Financial clarity for professional services firms",
        subheadline: "Enforce engagement approvals, trust accounting, and collections discipline — time, billing, and receivables in one authority-driven PSA.",
        primaryCta: "Book a Demo",
        secondaryCta: "See Pricing"
      },
      features: [
        {
          id: "engagement",
          title: "Engagement Management",
          description: "Scope, track, and manage client engagements with role-based visibility. Organize matters, files, and team assignments with clear permissions.",
          highlights: [
            "Client and matter tracking",
            "Team assignments with role-based access",
            "File management and document workflows",
            "Engagement-level permissions"
          ]
        },
        {
          id: "time",
          title: "Time & Expense Tracking",
          description: "Daily logs, weekly summaries, and productivity insights. Track work hours with precision and maintain real-time visibility into unbilled time.",
          highlights: [
            "Daily time entry with compliance monitoring",
            "Cash advance submission and Expense Liquidation reconciliation",
            "Expense recovery and billing integration",
            "PH-style approval processes"
          ]
        },
        {
          id: "billing",
          title: "Billing & Invoicing",
          description: "Generate invoices from engagements or timesheets. Support for time-based, fixed, contingency, and recurring billing methods.",
          highlights: [
            "Multiple billing methods supported",
            "Semantic validation prevents errors",
            "Expense recovery integration",
            "Automated or manual billing workflows"
          ]
        },
        {
          id: "refunds",
          title: "Cash Return Management",
          description: "Process refunds, reversals, and adjustments with multi-level approvals. Keep the audit trail tight for client disputes and trust-fund reconciliation.",
          highlights: [
            "Refund and reversal workflows",
            "Multi-level approval gates",
            "Trust-fund adjustment integration",
            "Full audit trail on every transaction"
          ]
        },
        {
          id: "disbursements",
          title: "Partner Disbursements",
          description: "Distribute payments to subcontractors, co-counsel, and experts with engagement-level tracking, approval workflows, and full reconciliation against client billings.",
          highlights: [
            "Engagement-scoped disbursement records",
            "Multi-level approval before payout",
            "Reconciliation against client invoices and cash advances",
            "Complete audit trail for every peso out"
          ]
        },
        {
          id: "collections",
          title: "AR & Collections",
          description: "Track payments, aging, and collection follow-through. The Collections Desk provides a complete workstation for managing receivables.",
          highlights: [
            "AR dashboard with aging buckets",
            "Collections Desk for systematic follow-up",
            "Write-off workflows with approvals",
            "Activity logging and escalation tracking"
          ]
        },
        {
          id: "trust",
          title: "Trust Accounting",
          description: "Keep client funds separated, compliant, and fully reconciled. Deposit, allocate, and draw down with strict compliance controls.",
          highlights: [
            "Client deposit management",
            "Drawdown to invoices and expenses",
            "Reconciliation monitoring",
            "Earmarking and audit trails"
          ]
        },
        {
          id: "reporting",
          title: "Reporting & Intelligence",
          description: "Firm-wide insights with executive dashboards, aging reports, and compliance analytics. Export data when you need it.",
          highlights: [
            "Collections and billing dashboards",
            "Aging reports by 30/60/90+ days",
            "Compliance analytics",
            "CSV exports for all reports"
          ]
        },
        {
          id: "controls",
          title: "Period Controls",
          description: "Lock engagement financials at month-end and quarter-end. Prevent retroactive entries, enforce compliance windows, and close with confidence.",
          highlights: [
            "Date-driven period lockdown",
            "Retroactive-entry prevention",
            "Closure exceptions with approval",
            "Audit-ready period snapshots"
          ]
        }
      ],
      pricing: [
        {
          name: "Starter",
          description: "For small firms getting started with professional services automation",
          price: "PHP 990",
          period: "per user/month",
          features: [
            "Up to 10 users",
            "Engagement management",
            "Time & expense tracking",
            "Basic billing",
            "Collections dashboard",
            "Email support"
          ],
          cta: "Book a Demo",
          highlighted: false
        },
        {
          name: "Growth",
          description: "For growing firms that need advanced financial controls and reporting",
          price: "PHP 1,490",
          period: "per user/month",
          features: [
            "Up to 50 users",
            "Everything in Starter",
            "Advanced reporting",
            "Trust accounting",
            "Multi-level approvals",
            "Collections Desk",
            "Priority support"
          ],
          cta: "Book a Demo",
          highlighted: true
        },
        {
          name: "Enterprise",
          description: "For large firms requiring custom integrations and dedicated support",
          price: "Custom",
          period: "tailored to your needs",
          features: [
            "Unlimited users",
            "Everything in Growth",
            "Custom integrations",
            "API access",
            "Dedicated success manager",
            "Custom training",
            "SLA guarantees"
          ],
          cta: "Contact Sales",
          highlighted: false
        }
      ],
      industries: ['law', 'consulting', 'engineering']
    },

    wms: {
      name: 'Klarhead WMS',
      fullName: 'Warehouse Management System',
      tagline: 'Warehouse Operations, Clarified',
      slug: 'wms',
      accent: 'amber',
      hero: {
        headline: "Warehouse operations, clarified",
        subheadline: "Real-time inventory visibility, lot-level traceability, and compliance-ready operations for Philippine warehouses and distribution centers.",
        primaryCta: "Book a Demo",
        secondaryCta: "See Pricing",
        badge: "Design Partner Program"
      },
      features: [
        {
          id: "inventory",
          title: "Inventory Management",
          description: "Real-time stock visibility across all warehouse locations. Track lots, batches, and expiry dates with automatic FIFO enforcement.",
          highlights: [
            "Multi-warehouse, location-level tracking",
            "Lot and batch traceability",
            "Expiry alerts and FIFO enforcement",
            "Cycle counting and audit-ready reporting"
          ]
        },
        {
          id: "serialization",
          title: "Serial Number Tracking",
          description: "Track individual units from receiving through dispatch. Built for electronics, high-value goods, and compliance-heavy inventory where unit-level traceability matters.",
          highlights: [
            "Serial capture at receiving and dispatch",
            "Unit-level movement history",
            "Warranty and recall audit support",
            "Coexists with lot and batch tracking"
          ]
        },
        {
          id: "supplier",
          title: "Supplier Portal",
          description: "Give your suppliers self-service visibility into POs, ASNs, and delivery commitments. Cut back-and-forth emails and accelerate receiving.",
          highlights: [
            "PO status visibility for suppliers",
            "Advance Shipment Notice (ASN) submission",
            "Role-scoped access — suppliers see only their own data",
            "Audit trail on every supplier action"
          ]
        },
        {
          id: "receiving",
          title: "Receiving & Putaway",
          description: "Streamlined inbound processing with PO matching, quality checks, and directed putaway optimization.",
          highlights: [
            "GRN receiving with PO matching",
            "Put-away with scan confirmation",
            "Quality inspection workflows",
            "Directed putaway optimization"
          ]
        },
        {
          id: "picking",
          title: "Picking & Packing",
          description: "Efficient order fulfillment with batch picking, scan verification, and pack confirmation.",
          highlights: [
            "Batch and location-optimized pick lists",
            "Barcode scan verification",
            "Pack verification workflows",
            "Replenishment task management"
          ]
        },
        {
          id: "replenishment",
          title: "Replenishment & Stock Management",
          description: "Automated replenishment driven by reorder points and pick-face demand. Keep bins stocked without over-committing inventory.",
          highlights: [
            "Reorder-point-driven replenishment tasks",
            "Directed bin-to-bin movement",
            "Pick-face readiness tracking",
            "Integrates with cycle counts and receiving"
          ]
        },
        {
          id: "dispatch",
          title: "Dispatch Management",
          description: "Shipment tracking and waybill capture for complete outbound visibility.",
          highlights: [
            "Waybill capture and tracking",
            "Shipment status monitoring",
            "Dispatch scheduling",
            "Proof of dispatch records"
          ]
        },
        {
          id: "returns",
          title: "Returns, Quality & Damaged Goods Disposal",
          description: "Complete reverse logistics plus a distinct Damaged Goods Disposal workflow for write-offs, donations, and regulated disposal — authority-backed and audit-ready.",
          highlights: [
            "Return authorization and grading",
            "Quarantine and hold management",
            "Damaged Goods Disposal with inspection, approval, and write-off stages",
            "Complete audit trail for compliance and insurance claims"
          ]
        },
        {
          id: "traceability",
          title: "Traceability & Compliance",
          description: "Forward and backward lot tracing with complete audit trails for regulatory compliance.",
          highlights: [
            "Forward/backward traceability",
            "Recall simulation and execution",
            "Immutable transaction trails",
            "Compliance-ready documentation"
          ]
        }
      ],
      pricing: [
        {
          name: "Operations",
          description: "Core WMS + document management — shipped and ready",
          price: "PHP 45,000",
          period: "/month",
          setupPrice: "PHP 150,000 setup",
          quoteLed: false,
          features: [
            "10 users included",
            "Unlimited SKUs & transactions",
            "Full warehouse execution",
            "Sales Documents (SI/DR) for outbound fulfillment",
            "Stock Release workflow for internal warehouse movements",
            "Lot traceability & FIFO",
            "Mobile PWA & barcode scanning",
            "Cycle counts & reporting",
            "Business hours support"
          ],
          cta: "Book a Demo",
          highlighted: false
        },
        {
          name: "Control",
          description: "Integration-ready operations layer — scope varies",
          price: "Custom",
          period: "",
          setupPrice: "Quoted based on scope",
          quoteLed: true,
          features: [
            "Everything in Operations",
            "Retail partner integration",
            "E-commerce sync*",
            "Compliance automation",
            "API access",
            "Priority support",
            "Dedicated success manager"
          ],
          footnote: "*CSV import available today. Native marketplace connectors planned for H2 2026.",
          cta: "Get Quote",
          highlighted: true
        },
        {
          name: "Enterprise",
          description: "Custom scope, SLA, and compliance requirements",
          price: "Contact Sales",
          period: "",
          setupPrice: "Custom implementation",
          quoteLed: true,
          features: [
            "Everything in Control",
            "Custom SLA guarantees",
            "SSO/SAML integration (roadmap — timeline on request)",
            "On-premise option",
            "Custom compliance",
            "Dedicated CSM"
          ],
          cta: "Contact Sales",
          highlighted: false
        }
      ],
      industries: ['3pl', 'distribution']
    }
  },

  valuePillars: [
    {
      title: "Financial Clarity",
      description: "Real-time visibility into billable work, aged receivables, and client deposits. Know exactly where your revenue stands at any moment.",
      icon: "chart"
    },
    {
      title: "Operational Discipline",
      description: "Structured workflows enforce compliance without slowing work. Multi-level approvals, Expense Liquidation tracking, and cash-advance recovery built in.",
      icon: "workflow"
    },
    {
      title: "Deterministic Trust",
      description: "Every transaction is traceable, every balance is reconcilable. Complete audit trails from time entry to collection.",
      icon: "shield"
    },
    {
      title: "PSA + Governance Fusion",
      description: "Not just project management — financial governance embedded at every step. Billing semantic validation ensures accuracy.",
      icon: "integration"
    }
  ],

  wmsValuePillars: [
    {
      title: "Inventory Accuracy",
      description: "Real-time stock visibility across all locations. Know exactly what you have, where it is, and when it expires.",
      icon: "chart"
    },
    {
      title: "Operational Discipline",
      description: "Structured workflows enforce accuracy at every step. Scan verification, directed putaway, and pick optimization built in.",
      icon: "workflow"
    },
    {
      title: "Complete Traceability",
      description: "Every movement is tracked, every lot is traceable. Full audit trails from receiving to shipment.",
      icon: "shield"
    },
    {
      title: "Compliance Ready",
      description: "Built for Philippine regulatory requirements. Lot tracking, expiry management, and recall support standard.",
      icon: "integration"
    }
  ],

  testimonials: [
    {
      quote: "Klarhead transformed how we manage our receivables. We now have complete visibility into our aged AR and can follow up systematically instead of chasing invoices at month-end.",
      author: "Finance Director",
      company: "Professional Services Firm",
      role: "CFO",
      product: "psa",
      status: "live"
    },
    {
      quote: "We finally have real-time inventory visibility across every location. Lot traceability used to take hours; now it's a scan.",
      author: "Operations Manager",
      company: "Mid-market Distributor",
      role: "Operations Manager",
      product: "wms",
      status: "placeholder"
    }
  ],

  stats: [
    { value: "99.9%", label: "Platform Uptime" },
    { value: "Complete", label: "Audit Trails" },
    { value: "Real-time", label: "Financial Visibility" },
    { value: "GAAP", label: "Compliant Accounting" }
  ],

  wmsStats: [
    { value: "99.9%", label: "Platform Uptime" },
    { value: "Complete", label: "Lot Traceability" },
    { value: "Real-time", label: "Inventory Visibility" },
    { value: "FIFO", label: "Enforced Automatically" }
  ],

  trustSignals: {
    security: [
      "HTTPS encryption for all data in transit",
      "Role-based access controls",
      "Complete audit trails for every transaction",
      "Tenant isolation for data separation"
    ],
    compliance: [
      "GAAP-compliant accounting workflows",
      "Deterministic approval processes",
      "Reconciliation monitoring with invariants",
      "Document retention controls"
    ],
    reliability: [
      "99.9% platform uptime",
      "Regular automated backups",
      "Disaster recovery procedures",
      "Performance monitoring"
    ]
  }
}

export const industries = {
  // PSA Industries
  law: {
    title: "Built for Legal Practice Management",
    description: "Klarhead helps law firms manage matters, track billable hours, maintain trust accounts, and collect fees with the compliance rigor your practice demands.",
    product: "psa",
    challenges: [
      "Trust account compliance and reconciliation",
      "Matter-based time tracking across attorneys",
      "Complex billing arrangements (retainer, contingency, hourly)",
      "Client fund segregation and audit trails"
    ],
    solutions: [
      "Dedicated trust accounting with reconciliation monitoring",
      "Matter-centric engagement management",
      "Multiple billing methods with semantic validation",
      "Complete audit trails for bar compliance"
    ]
  },
  consulting: {
    title: "Professional Services Automation for Consultancies",
    description: "Klarhead gives consulting firms control over project profitability, resource utilization, and expense management with real-time financial visibility.",
    product: "psa",
    challenges: [
      "Tracking utilization across consultants and projects",
      "Managing client expenses and reimbursements",
      "Billing accuracy for time-and-materials engagements",
      "Visibility into project profitability"
    ],
    solutions: [
      "Comprehensive time tracking with compliance monitoring",
      "Expense management with cash advance + liquidation workflows",
      "Automated billing from tracked time",
      "Real-time AR and collections visibility"
    ]
  },
  engineering: {
    title: "Project Control for Engineering Firms",
    description: "Klarhead provides engineering and architecture firms with the engagement tracking, retainer management, and financial controls needed for complex, multi-phase projects.",
    product: "psa",
    challenges: [
      "Managing multi-phase project billing",
      "Tracking retainers across long-running engagements",
      "Coordinating expenses across project teams",
      "Maintaining documentation for compliance"
    ],
    solutions: [
      "Flexible engagement structures for phases and milestones",
      "Client deposit management with drawdown tracking",
      "Team-based expense submission and approval",
      "Document management with permissions"
    ]
  },

  // WMS Industries
  '3pl': {
    title: "Built for 3PL & Fulfillment Operations",
    description: "Klarhead WMS gives third-party logistics providers the multi-client inventory management, billing accuracy, and SLA tracking needed for fulfillment excellence.",
    product: "wms",
    challenges: [
      "Managing inventory for multiple clients in shared space",
      "Accurate billing for storage and handling activities",
      "SLA compliance tracking and reporting",
      "Client-specific workflow requirements"
    ],
    solutions: [
      "Client segregation with dedicated inventory pools",
      "Activity-based tracking for billable events",
      "SLA monitoring with compliance dashboards",
      "Configurable workflows per client"
    ]
  },
  distribution: {
    title: "Built for Distribution & Wholesale",
    description: "Klarhead WMS provides distributors and wholesalers with the inventory accuracy, partner management, and B2B controls needed for efficient distribution operations.",
    product: "wms",
    challenges: [
      "Stock accuracy across multiple locations",
      "Partner and dealer management",
      "Order fulfillment speed and accuracy",
      "Expiry and FIFO compliance for perishables"
    ],
    solutions: [
      "Real-time inventory with location-level visibility",
      "Partner tiers with credit controls",
      "Stock Release workflow",
      "Automatic FIFO enforcement with expiry alerts"
    ]
  }
}

// Backward compatibility exports
export const hero = siteContent.products.psa.hero
export const features = siteContent.products.psa.features
export const pricingTiers = siteContent.products.psa.pricing
